import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { createCheckoutSession, redirectToCheckout } from '../utils/stripe';
import { FREE_SHIPPING_THRESHOLD } from '../config/products';
import { trackEvent } from '../utils/analytics';

const CartContext = createContext();

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

function loadCart() {
  try {
    const raw = localStorage.getItem('honeyCart');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    localStorage.removeItem('honeyCart');
    return [];
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(loadCart);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem('honeyCart', JSON.stringify(cart));
    } catch { /* quota exceeded — silent fail */ }
  }, [cart]);

  const addToCart = useCallback((product, quantity = 1) => {
    if (!product?.id) {
      setCheckoutError('Unable to add this item. Please try again.');
      return;
    }

    setCart(prev => {
      const key = product.id;
      const idx = prev.findIndex(i => i.id === key);

      if (idx >= 0) {
        return prev.map((item, i) =>
          i === idx ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { ...product, quantity }];
    });

    trackEvent('add_to_cart', {
      currency: 'USD',
      value: product.price * quantity,
      items: [{ item_id: product.id, item_name: product.name, price: product.price, quantity }],
    });

    setIsCartOpen(true);
    setCheckoutError(null);
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => {
      const removed = prev.find(item => item.id === productId);
      if (removed) {
        trackEvent('remove_from_cart', {
          currency: 'USD',
          value: removed.price * removed.quantity,
          items: [{ item_id: removed.id, item_name: removed.name, price: removed.price, quantity: removed.quantity }],
        });
      }
      return prev.filter(item => item.id !== productId);
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => setCart([]), []);
  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);
  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const shippableTotal = cart
      .filter(item => !item.pickup)
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
    const hasShippableItems = shippableTotal > 0;
    const freeShipping = hasShippableItems && shippableTotal >= FREE_SHIPPING_THRESHOLD;
    const shippingProgress = hasShippableItems
      ? Math.min(shippableTotal / FREE_SHIPPING_THRESHOLD, 1)
      : 0;

    return { subtotal, itemCount, freeShipping, shippingProgress, hasShippableItems, shippableTotal };
  }, [cart]);

  const proceedToCheckout = useCallback(async () => {
    if (cart.length === 0) {
      setCheckoutError('Your cart is empty');
      return;
    }

    try {
      setIsCheckingOut(true);
      setCheckoutError(null);

      trackEvent('begin_checkout', {
        currency: 'USD',
        value: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
        items: cart.map(item => ({ item_id: item.id, item_name: item.name, price: item.price, quantity: item.quantity })),
      });

      const url = await createCheckoutSession(cart);
      redirectToCheckout(url);
    } catch (error) {
      setCheckoutError(error.message || 'Unable to process checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    isCartOpen,
    isCheckingOut,
    checkoutError,
    totals,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    proceedToCheckout,
  }), [
    cart, isCartOpen, isCheckingOut, checkoutError, totals,
    addToCart, removeFromCart, updateQuantity, clearCart,
    toggleCart, openCart, closeCart, proceedToCheckout,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
